import {AfterViewInit, Component, OnInit, ViewChild} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {debounce, filter, Observable, Subject, switchMap, takeUntil, timer} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ApiUsersListResponse} from '../services/http/api-users-list-response';
import {ApiUsersListItemResponse} from '../services/http/api-users-list-item-response';
import {ApiUsersListPaginatedQueryParams} from '../services/http/api-users-list-paginated-query-params';
import {UserRestApiService} from '../services/http/user-rest-api.service';
import {MatSort, SortDirection} from '@angular/material/sort';
import {PageEvent} from '@angular/material/paginator';
import {RoutesEnum} from '../../../core/services/navigation/routes.enum';
import {UsersRoutesEnum} from '../services/navigation/users-routes.enum';
import {FormBuilder, FormControl} from '@angular/forms';
import {UserRolesEnum} from '../../../core/services/user/user-roles.enum';
import {UserStatusesEnum} from '../../../core/services/user/user-statuses.enum';
import {ApiRefreshTimerEnum} from '../../../core/services/http/api-refresh-timer.enum';
import {TranslocoService} from '@jsverse/transloco';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatDialog} from '@angular/material/dialog';
import {ConfirmDialogComponent} from '../../../core/components/confirm-dialog/confirm-dialog.component';
import {ConfirmDialogData} from '../../../core/components/confirm-dialog/confirm-dialog-data';
import {HealthCenterRestApiService} from '../../health-center/services/http/health-center-rest-api.service';
import {ApiHealthCentersShortListItemResponse}
  from '../../health-center/services/http/api-health-centers-short-list-item-response';

@Component({
  selector: 'xp-users-list',
  templateUrl: './users-list.component.html',
  styleUrls: ['../../crud.common.scss', './users-list.component.scss'],
})
export class UsersListComponent implements OnInit, AfterViewInit {

  public isLoading: boolean = true;

  public users: Array<ApiUsersListItemResponse> | null = null;

  public itemsCount: number = 0;
  public pageIndex: number = 0;
  public pageSize: number = 10;

  public displayedColumns: Array<string> = [
    'email',
    'firstName',
    'lastName',
    'role',
    'healthCenter',
    'status',
    'actions',
  ];

  public readonly formGroup = this._formBuilder.group({
    search: new FormControl<string>(''),
    statuses: new FormControl<Array<UserStatusesEnum>>(this.statusesList),
    roles: new FormControl<Array<UserRolesEnum>>(this.rolesList),
  });

  public errorKey: string = '';
  public defaultSortOn: string = 'email';
  public defaultSortOrder: SortDirection = 'asc';
  @ViewChild(MatSort)
  public sort: MatSort = new MatSort();

  private _requestUsersChangeSubject: Subject<number> = new Subject<number>();

  private _healthCenterNames: Map<string, string> = new Map<string, string>();

  constructor(
    private readonly _usersRestApiService: UserRestApiService,
    private readonly _healthCenterRestApiService: HealthCenterRestApiService,
    private readonly _formBuilder: FormBuilder,
    private readonly _translocoService: TranslocoService,
    private readonly _snackBar: MatSnackBar,
    private readonly _dialog: MatDialog,
  ) {
    this._requestUsersChangeSubject.pipe(
      takeUntilDestroyed(),
      debounce((dueTime: number) => timer(dueTime)),
      switchMap(() => this.requestUsers().pipe(
        takeUntil(this._requestUsersChangeSubject),
      )),
    ).subscribe({
      next: (response: ApiUsersListResponse) => this.handleRequestUsersResponse(response),
    });
  }

  public get createAccountRoute(): Array<string> {
    return ['/', RoutesEnum.USERS, UsersRoutesEnum.CREATE];
  }

  public get searchValue(): string {
    return this.formGroup.controls.search.value?.trim() ?? '';
  }

  public get statusesList(): Array<UserStatusesEnum> {
    return Object.values(UserStatusesEnum);
  }

  public get disabledStatus(): string {
    return UserStatusesEnum.DISABLED;
  }

  public get rolesList(): Array<UserRolesEnum> {
    return Object.values(UserRolesEnum);
  }

  public ngOnInit(): void {
    this.requestHealthCenterNames();
    this._requestUsersChangeSubject.next(ApiRefreshTimerEnum.INSTANTLY);
  }

  public ngAfterViewInit(): void {
    // Default value of mat-select is not working in case of F5, so we set it here
    setTimeout(() => {
      this.setDefaultFilterValues();
    }, 200);
  }

  public editAccountRoute(id: string): string {
    return ['/', RoutesEnum.USERS, UsersRoutesEnum.EDIT.replace(':id', id)].join('/');
  }

  public healthCenterName(healthCenterId: null|string): string {
    if (healthCenterId === null) {
      return '';
    }
    return this._healthCenterNames.get(healthCenterId) ?? '';
  }
  public onSearchInput(): void {
    this.pageIndex = 0;
    this._requestUsersChangeSubject.next(ApiRefreshTimerEnum.KEYBOARD_INPUT);
  }

  public onFilterChange(): void {
    this.pageIndex = 0;
    this._requestUsersChangeSubject.next(ApiRefreshTimerEnum.MOUSE_CLICK);
  }

  public clearSearch(): void {
    this.pageIndex = 0;
    this.formGroup.controls.search.setValue('');
    this._requestUsersChangeSubject.next(ApiRefreshTimerEnum.INSTANTLY);
  }

  public resetFilters(): void {
    this.setDefaultFilterValues();
    this.clearSearch();
    this.formGroup.markAsPristine();
  }

  public onSortChange(): void {
    this._requestUsersChangeSubject.next(ApiRefreshTimerEnum.MOUSE_CLICK);
  }

  public onPaginatorChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this._requestUsersChangeSubject.next(ApiRefreshTimerEnum.INSTANTLY);
  }

  public enableUser(index: number, enabled: number): void {
    if (!this.users) {
      return;
    }

    const user = this.users[index];

    this._usersRestApiService.requestEnable(user.id, enabled)
      .subscribe({
        next: () => {
          user.deletedAt = enabled === 0 ? new Date() : null;
          this._snackBar.open(
            this._translocoService.translate(
              'USERS.UPDATE.SUCCESS',
              {email: user?.email},
            ),
            '',
            {duration: 7000},
          );
        },
        error: () => this.handleError(),
      });
  }

  public deleteUser(index: number): void {
    if (!this.users) {
      return;
    }

    const user = this.users[index];
    const dialogData: ConfirmDialogData = {
      title: 'USERS.DELETE_CONFIRM_DIALOG.TITLE',
      message: 'USERS.DELETE_CONFIRM_DIALOG.MESSAGE',
      confirmTextKey: 'USERS.DELETE_CONFIRM_DIALOG.CONFIRM',
      cancelTextKey: 'USERS.DELETE_CONFIRM_DIALOG.CANCEL',
    };

    this._dialog.open(ConfirmDialogComponent, {data: dialogData}).afterClosed().pipe(
      filter(Boolean),
      switchMap(() => this._usersRestApiService.requestDelete(user.id)),
    ).subscribe({
      next: () => {
        this.users = this.users!.filter((_, i) => i !== index);
        this.itemsCount--;
        this._snackBar.open(
          this._translocoService.translate('USERS.DELETE_CONFIRM_DIALOG.SUCCESS', {email: user.email}),
          '',
          {duration: 7000},
        );
      },
      error: (err: HttpErrorResponse) => {
        if (err.error?.code === 'CANNOT_DELETE_OWN_ACCOUNT') {
          this._snackBar.open(
            this._translocoService.translate('USERS.DELETE_CONFIRM_DIALOG.CANNOT_DELETE_OWN_ACCOUNT'),
            '',
            {duration: 7000, panelClass: 'snackbar-error'},
          );
        } else {
          this.handleError();
        }
      },
    });
  }

  private requestHealthCenterNames(): void {
    this._healthCenterRestApiService.requestHealthCenterShortListOfName()
      .subscribe({
        next: (healthCenters: Array<ApiHealthCentersShortListItemResponse>) => {
          this._healthCenterNames = new Map<string, string>(
            healthCenters.map((healthCenter: ApiHealthCentersShortListItemResponse) =>
              [healthCenter.id, healthCenter.name]),
          );
        },
        error: () => this.handleError(),
      });
  }

  private requestUsers(): Observable<ApiUsersListResponse> {
    this.isLoading = true;
    const queryParams: ApiUsersListPaginatedQueryParams = {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      sort: this.sort?.active ?? this.defaultSortOn,
      order: this.sort?.direction ?? this.defaultSortOrder,
      search: this.searchValue,
      roles: this.formGroup.controls.roles.value ?? [],
      statuses: this.formGroup.controls.statuses.value ?? [],

    };
    return this._usersRestApiService.requestUsersListPaginated(queryParams);
  }

  private handleRequestUsersResponse(response: ApiUsersListResponse): void {
    this.isLoading = false;
    this.itemsCount = response.totalCount;
    this.users = response.users;
  }

  private setDefaultFilterValues(): void {
    this.formGroup.controls.statuses.setValue(this.statusesList);
    this.formGroup.controls.roles.setValue(this.rolesList);
  }

  private handleError(): void {
    this.errorKey = 'SHARED.ERRORS.UNKNOWN';
  }
}
